import type { FC } from 'react';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { Button, Stack } from '@mui/material';
import type { LineItem, TaxItem } from '../ServiceProviderReceiptBatchForm';
import { v4 as uuid } from 'uuid';
import { LineItemAmountInput } from './LineItemAmountInput';
import { useGetCountryOfServiceProvider } from '../../../../hooks/useGetCountryOfServiceProvider';
import {
  useGetTaxCodesByCountry,
  useGetTaxCodesByCountryPhoenix,
  useGetTaxLineConfigurations,
  useGetTaxLineConfigurationsPhoenix,
} from '@pflegenavi/service-provider-frontend/api';
import { useGetGlobalServiceProviderId } from '../../../../hooks/useGetGlobalServiceProviderId';
import { ServiceProviderQuickResidentsServiceSelection } from './ServiceProviderQuickResidentsServiceSelection';
import { ServiceProviderReceiptBatchTaxSelection } from '../ServiceProviderReceiptBatchTaxSelection';
import { useTranslation } from 'react-i18next';
import { sanitizeString } from '@pflegenavi/shared/utils';
import { useReceiptBatchStore } from '../../ReceiptBatchContext';
import { useShallow } from 'zustand/react/shallow';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';

interface QuickResidentsSelectionLineItemFormProps {
  lineItems: LineItem[];
  addLineItem: (lineItem: LineItem) => void;
}

export const QuickResidentsSelectionLineItemForm: FC<QuickResidentsSelectionLineItemFormProps> =
  // eslint-disable-next-line complexity
  memo(({ lineItems, addLineItem }) => {
    const { t } = useTranslation();

    const { globalServiceProviderId } = useGetGlobalServiceProviderId();

    const countryId = useGetCountryOfServiceProvider();
    const usePhoenix = useFeatureFlagEnabled(
      FeatureFlag.dev_ReceiptBatchPhoenix
    );
    const { data: taxConfigurations } = (
      usePhoenix ? useGetTaxCodesByCountryPhoenix : useGetTaxCodesByCountry
    )(countryId);
    const { data: lineItemConfigurations } = (
      usePhoenix
        ? useGetTaxLineConfigurationsPhoenix
        : useGetTaxLineConfigurations
    )(globalServiceProviderId);

    const [autoGenerateReceipts, defaultTaxCodeId] = useReceiptBatchStore(
      useShallow((state) => [
        state.formik?.values?.generateReceipts ?? false,
        state.serviceProvider?.defaultTaxCodeId,
      ])
    );

    const taxItems = useMemo<Array<TaxItem & { id: string }>>(
      () =>
        taxConfigurations?.map((taxConfiguration) => ({
          id: taxConfiguration.id,
          taxName: taxConfiguration.name,
          countryId: taxConfiguration.countryId,
          displayExplanation: taxConfiguration.displayTaxRatioExplanation,
          taxAmount: taxConfiguration.taxRatio / 100,
          taxAmountInPercentage: taxConfiguration.taxRatio,
          explanationLong: taxConfiguration.taxRatioExplanation,
          // Todo: Currently there is no short explanation in the tax configuration
          explanationShort: undefined,
        })) ?? [],
      [taxConfigurations]
    );

    const defaultTaxCode = useMemo(() => {
      return taxItems.find((tax) => tax.id === defaultTaxCodeId);
    }, [taxItems, defaultTaxCodeId]);

    const newLineItem = useMemo<LineItem>(() => {
      return {
        id: undefined,
        _internalId: '',
        name: '',
        amount: undefined,
        count: 1,
        taxes: defaultTaxCode ? [defaultTaxCode] : undefined,
      };
    }, [defaultTaxCode]);

    const [lineItemToAdd, setLineItemToAdd] = useState<LineItem>(newLineItem);

    const handleSetAmount = useCallback((amount: number | undefined) => {
      setLineItemToAdd((prev) => ({
        ...prev,
        amount,
      }));
    }, []);

    const handleSetName = useCallback(
      (name: string) => {
        setLineItemToAdd({
          ...lineItemToAdd,
          name,
        });
      },
      [lineItemToAdd]
    );

    const handleSetTax = useCallback((tax: TaxItem | undefined) => {
      setLineItemToAdd((prev) => ({
        ...prev,
        taxes: tax ? [tax] : undefined,
      }));
    }, []);

    const handleAddLineItem = useCallback(() => {
      const sanitizedLineItem = {
        ...lineItemToAdd,
        _internalId: `new-${uuid()}`,
        name: sanitizeString(lineItemToAdd.name ?? ''),
      };
      addLineItem(sanitizedLineItem);
      setLineItemToAdd(newLineItem);
    }, [addLineItem, lineItemToAdd, newLineItem]);

    const isAddButtonDisabled = useMemo(() => {
      const buttonDisabledDueToTax = autoGenerateReceipts
        ? !lineItemToAdd.taxes?.length
        : false;

      return (
        !lineItemToAdd.name || buttonDisabledDueToTax || lineItems.length >= 10
      );
    }, [
      autoGenerateReceipts,
      lineItemToAdd.taxes?.length,
      lineItemToAdd.name,
      lineItems.length,
    ]);

    if (!taxConfigurations || !lineItemConfigurations) {
      return null;
    }

    return (
      <Stack gap={1} width="100%">
        <Stack>
          <ServiceProviderQuickResidentsServiceSelection
            lineItemConfigurations={lineItemConfigurations}
            lineItem={lineItemToAdd}
            setLineItem={setLineItemToAdd}
            setName={handleSetName}
            textFieldProps={{
              placeholder: t(
                'receipts.batch.form.resident-quick-selection.line-items.line-item-name'
              ),
            }}
          />
          {autoGenerateReceipts && (
            <ServiceProviderReceiptBatchTaxSelection
              disabled={
                lineItemToAdd.name ? lineItemToAdd.name.length === 0 : true
              }
              handleTaxItemChange={handleSetTax}
              selectedTaxItem={
                lineItemToAdd.taxes ? lineItemToAdd.taxes[0] : undefined
              }
              taxItems={taxItems}
            />
          )}
        </Stack>

        <LineItemAmountInput
          value={lineItemToAdd.amount}
          handleSetLineItemAmount={handleSetAmount}
          handleResetLineItemAmount={() => handleSetAmount(undefined)}
        />
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            disabled={isAddButtonDisabled}
            onClick={handleAddLineItem}
          >
            {t(
              'receipts.batch.form.resident-quick-selection.line-items.add-line-item-button'
            )}
          </Button>
        </Stack>
      </Stack>
    );
  });
